import React, { useEffect, useState, useCallback, Fragment, ComponentType } from 'react';
import Select, {
  MultiValue,
  GroupBase,
  StylesConfig,
  components,
  MenuListProps
} from 'react-select';
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
  Textarea,
  Typography
} from '@material-tailwind/react';
import Button from '../../library/Button/button';
import IUserList from '../../utils/UserList/UserList.interface';
import OrganizationAdmin from '../../utils/OrganizationAdmin/OrganizationAdmin';
import AllOrganizationAdmin from '../../utils/OrganizationUser/AllOrganisationUser';
import SubmitBroadcastMessage from '../../utils/Broadcast/Broadcast';
import CustomPopup from './CustomPopup';
import { responseMessages } from '../../utils/Helper/enums';
import { handleApiResponse } from '../../utils/Helper/apiResponseHandler';
import PreviewMessage from '../../library/PreviewMessage/previewMessage';
import { useAppSelector } from '../../hooks/storeHooks';

/**
 * BroadCastMessage Component
 *
 * This React functional component provides an interface for sending broadcast messages
 * (e.g., announcements or alerts) to organization administrators.
 *
 * Core Functionalities:
 * - Allows composing a subject and message body.
 * - Lets users select multiple organizations from a dropdown (multi-select).
 * - Automatically fetches admin email addresses for selected organizations.
 * - Provides a "Select All" feature to select all organizations quickly.
 * - Displays the recipient list with the ability to remove individual emails.
 * - Validates required fields (subject, message, recipients) before sending.
 * - Sends the composed message to the selected admin emails via a utility class.
 * - Displays success or error feedback in a modal popup.
 *
 * Dependencies:
 * - Uses utility classes: OrganizationAdmin, AllOrganizationAdmin, SubmitBroadcastMessage.
 * - Integrates Material Tailwind components and react-select for UI.
 *
 * Intended for use by system administrators or privileged users to communicate with
 * organization partners in bulk.
 */

interface OptionType {
  value: string;
  label: string;
}

export const BroadCastMessage: React.FC = () => {
  const partnerState = useAppSelector((state) => state.partners);
  const partnerList = partnerState.data ?? [];
  const [open, setOpen] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState<OptionType[]>([]);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [errors, setErrors] = useState({ subject: false, select: false, message: false });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState(false);

  const toggleDialog = useCallback(() => {
    setOpen((prevOpen) => {
      if (prevOpen) {
        setSelectedOrganisation([]);
        setSelectedEmails([]);
        setMessage('');
        setSubject('');
        setErrors({ subject: false, select: false, message: false });
      }
      return !prevOpen;
    });
  }, []);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    setErrors((prev) => ({ ...prev, message: e.target.value.trim() === '' }));
  };

  const actualOptions: OptionType[] = partnerList
    .filter(
      (partner): partner is IUserList =>
        !!partner?.companyName && typeof partner.companyName === 'string'
    )
    .map((partner) => ({
      value: partner.companyName as string,
      label: partner.companyName as string
    }));

  const companyNameOptions: OptionType[] = actualOptions;

  const handleSelectChange = (selected: MultiValue<unknown>) => {
    const selectedOptions = selected as OptionType[];
    setSelectedOrganisation(selectedOptions);
    setErrors((prev) => ({ ...prev, select: selectedOptions.length === 0 }));

    const selectedCompanyNames = selectedOptions.map((opt) => opt.value);

    // First, remove emails of deselected organizations
    setSelectedEmails((prevEmails) => {
      const remainingEmails = prevEmails.filter((email) =>
        partnerList.some(
          (partner) =>
            partner.companyName &&
            selectedCompanyNames.includes(partner.companyName) &&
            partner.email === email
        )
      );
      return remainingEmails;
    });

    // Now, add emails of newly selected organizations
    selectedCompanyNames.forEach((companyName) => {
      partnerList.forEach((partner) => {
        if (partner.companyName === companyName) {
          const partnerEmail = partner.email ?? '';

          // Fetch emails from AllOrganizationAdmin
          const allOrgAdmin = new AllOrganizationAdmin({ email: partnerEmail });
          allOrgAdmin
            .getAllAdminUser()
            .then((adminUsers) => {
              const adminEmails = adminUsers
                .map((user) => user?.email)
                .filter((email): email is string => !!email);

              // Combine and deduplicate emails
              setSelectedEmails((prevEmails) => {
                const combinedEmails = [...prevEmails, partnerEmail, ...adminEmails];
                return Array.from(new Set(combinedEmails));
              });
            })
            .catch((error) => {
              console.error('Error fetching admin users from both sources:', error);
            });
        }
      });
    });

    // Close the dropdown when all options are selected
    if (selectedOptions.length === actualOptions.length) {
      setMenuIsOpen(false);
    }
  };

  const handleOnChange = () => {
    const newVal = actualOptions;
    setSelectedOrganisation(newVal);

    partnerList.forEach((partner) => {
      const email = partner.email ?? '';
      const allOrgAdmin = new AllOrganizationAdmin({ email });

      allOrgAdmin
        .getAllAdminUser()
        .then((emails) => {
          // Filter out null and undefined emails
          const validEmails = emails
            .map((user) => user?.email)
            .filter((email): email is string => !!email);

          setSelectedEmails((prev) => {
            // Combine previous emails with the new valid emails, deduplicate, and return
            const combinedEmails = [...prev, email, ...validEmails];
            return Array.from(new Set(combinedEmails));
          });
        })
        .catch((error) => {
          console.error('Error fetching admin users from both sources:', error);
        });
    });

    // Close the dropdown after selecting all
    setMenuIsOpen(false);
  };

  // Custom MenuList component to handle the Select All behavior
  function CustomMenuList(props: MenuListProps<unknown, boolean, GroupBase<unknown>>) {
    const { children, getValue } = props;
    const selected = getValue();
    const isAllSelected = selected.length === actualOptions.length;

    return (
      <components.MenuList {...props}>
        {!isAllSelected && (
          <div className="px-3 py-2 border-b border-gray-200">
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={isAllSelected} onChange={handleOnChange} />
              <span className="text-sm">Select All</span>
            </label>
          </div>
        )}
        {children}
      </components.MenuList>
    );
  }

  const handleRemoveEmail = (email: string) => {
    const updatedEmails = selectedEmails.filter((item) => item !== email);
    setSelectedEmails(updatedEmails);
  };

  const handleSendMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    setShowLoader(true);
    if (message.trim() === '' || selectedOrganisation.length === 0 || message.trim() === '') {
      setErrors({
        subject: message.trim() === '',
        select: selectedOrganisation.length === 0,
        message: message.trim() === ''
      });
      return;
    }

    // Message sending logic
    const userObj = new SubmitBroadcastMessage({
      subject,
      message,
      toEmails: selectedEmails
    });
    const broadCastMessageSubmissionStatus = await userObj.submitBroadcastMessage();
    setSubject('');
    setMessage('');
    setSelectedOrganisation([]);
    setSelectedEmails([]);

    const errorMessage = responseMessages.SomethingWentWrong;
    const successMessage = responseMessages.BroadcastMessageSubmitted;
    const response = handleApiResponse(
      broadCastMessageSubmissionStatus.statusCode,
      errorMessage,
      successMessage
    );
    const responseBody = response.body;
    setAlertMessage(responseBody);
    setShowAlert(true);
    setShowLoader(false);
  };

  const popupCloseAlertHandler = () => {
    setShowAlert(false);
    toggleDialog();
  };

  const customStyles: StylesConfig<unknown, true, GroupBase<OptionType>> = {
    control: (base, state) => ({
      ...base,
      borderColor: errors.select ? '#e60014' : 'black',
      boxShadow: state.isFocused
        ? errors.select
          ? '0 0 0 1px #e60014'
          : '0 0 0 1px black'
        : 'none',
      '&:hover': {
        borderColor: errors.select ? '#e60014' : 'black'
      },
      minHeight: '44px',
      maxHeight: '120px',
      overflowY: 'scroll',
      borderWidth: '1px',
      cursor: 'pointer'
    }),
    valueContainer: (base) => ({
      ...base,
      maxHeight: '100px',
      overflowY: 'scroll',
      flexWrap: 'wrap'
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '160px',
      overflowY: 'scroll'
    })
  };

  return (
    <Fragment>
      <Button
        className="text-white text-sm sm:text-base cursor-pointer bg-black rounded-lg mb-2 sm:mb-0 mr-0 sm:mr-2 py-2 px-4 w-full sm:w-auto min-w-[200px]"
        buttonText="📢 Broadcast Message"
        onclick={toggleDialog}
      />

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto px-4 py-6"
          onClick={toggleDialog}
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-lg shadow-lg mx-auto max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <DialogHeader className="flex flex-col items-start">
                <Typography className="mb-1" variant="h4">
                  New Announcement
                </Typography>
              </DialogHeader>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-5 w-9 cursor-pointer text-black"
                onClick={toggleDialog}
              >
                <path
                  fillRule="evenodd"
                  d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            <DialogBody>
              <Typography className="mb-10 -mt-7" color="gray" variant="lead">
                Write the message and then click button.
              </Typography>
              <div className="grid gap-4 sm:gap-6">
                <Typography className="-mb-3 required-field" color="blue-gray" variant="h6">
                  Subject:
                </Typography>
                <Input
                  className="rounded-lg w-full"
                  value={subject}
                  placeholder="Your subject here..."
                  onChange={(e) => setSubject(e.target.value)}
                  crossOrigin={false}
                />
                <Typography className="-mb-3 required-field" color="blue-gray" variant="h6">
                  {!errors.select ? (
                    'Select organisation:'
                  ) : (
                    <span>Please select at least one user</span>
                  )}
                </Typography>
                <Select
                  options={companyNameOptions}
                  value={selectedOrganisation}
                  isMulti
                  onChange={handleSelectChange}
                  onMenuOpen={() => setMenuIsOpen(true)}
                  onMenuClose={() => setMenuIsOpen(false)}
                  menuIsOpen={menuIsOpen}
                  styles={customStyles}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  components={{ MenuList: CustomMenuList }}
                />

                {selectedEmails.length > 0 && (
                  <div>
                    <Typography className="-mb-2 required-field" color="blue-gray" variant="h6">
                      Recipient List:
                    </Typography>
                    <br />
                    <div className="rounded-md border border-gray-300 px-3 py-2 bg-gray-50 h-auto max-h-40 overflow-y-auto shadow-sm">
                      <div className="flex flex-wrap gap-2">
                        {selectedEmails.map((email, index) => (
                          <div
                            key={index}
                            className="flex items-center bg-gray text-black px-3 py-1 rounded-full text-sm font-medium shadow-sm border border-black"
                          >
                            <span className="mr-1 break-words">{email}</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 cursor-pointer"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                              onClick={() => handleRemoveEmail(email)}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="relative items-center gap-2 -mb-4">
                  <Typography className="required-field" color="blue-gray" variant="h6">
                    {!errors.message ? (
                      'Message:'
                    ) : message.length > 500 ? (
                      <span>Message cannot exceed 500 characters</span>
                    ) : (
                      <span>Message cannot be empty</span>
                    )}
                  </Typography>
                  <PreviewMessage
                    message={message}
                    showPreview={showPreview}
                    setShowPreview={setShowPreview}
                    className="cursor-pointer text-gray-700 hover:text-black"
                  />
                </div>

                <Textarea
                  className="rounded-lg w-full h-[10rem]"
                  value={message}
                  placeholder="Your message here..."
                  onChange={handleMessageChange}
                  maxLength={500}
                  style={{ border: `1px solid ${errors.message ? '#e60014' : '#666'}` }}
                />
              </div>
            </DialogBody>

            <DialogFooter className="p-4 pt-2">
              <Button
                className={`text-[#fff] text-base bg-black rounded-lg py-2 px-4 z-[1100] ${
                  errors.subject ||
                  errors.message ||
                  errors.select ||
                  message === '' ||
                  subject === ''
                    ? 'cursor-not-allowed bg-opacity-50'
                    : ''
                }`}
                buttonText="Send Message"
                onclick={handleSendMessage}
                loading={showLoader}
                disabled={
                  errors.subject ||
                  errors.message ||
                  errors.select ||
                  message === '' ||
                  subject === ''
                }
              />
              <CustomPopup onClose={popupCloseAlertHandler} show={showAlert} width={'w-41%'}>
                <div className="max-h-30% ">
                  <p className="mr-0 mb-0.5 ml-0 text-[17px]">{alertMessage}</p>
                </div>
              </CustomPopup>
            </DialogFooter>
          </div>
        </div>
      )}
    </Fragment>
  );
};
